// shared-lib/dimensioning/marks.js
//
// The CENTRELINE mark — the one genuinely new drafting primitive this design
// needs (§5.3). A numberless `EQ` is just a label string and already draws; a
// centreline is a long-dash-short-dash line with no distance attached, and
// nothing in either renderer drew one before. Every "centerline" elsewhere in
// this repo is wall geometry for snapping, never an annotation.
//
// A mark is NOT a measurement and must not go through `layoutMeasurements`:
// that pass offsets a dimension line off its anchor, and a centreline's whole
// meaning is that it sits exactly ON the centre it marks.
//
// PURE.

const {
  resolveDatum,
  resolveAxis,
  perp,
  dotXZ,
  axisModeFor,
} = require('./datums');
const { resolveDimensions } = require('./rules');
const { normalizeRef } = require('../group-customization/refs');

// How far past the two things it relates a centreline runs, in metres. A
// centreline that stopped exactly at each edge would read as a dimension line.
const OVERRUN_M = 0.15;

const lookupEntity = (id, ctx) => {
  const byId = ctx?.entitiesById;
  if (!byId) return null;
  return typeof byId.get === 'function' ? byId.get(id) : byId[id];
};

function centreOf(ref, ctx) {
  const r = normalizeRef(ref);
  const c = r?.id ? lookupEntity(r.id, ctx)?.center : null;
  return c && typeof c.x === 'number' ? [c.x, c.z] : null;
}

/**
 * The centreline a record marks, or null when it marks none. PURE.
 *
 * Only a record that is ACTUALLY centred gets one — a drifted subject prints
 * its real numbers instead, and a centreline through two things that do not
 * share a centre would be a drawn lie.
 */
function centrelineFor(record, ctx) {
  if (record?.rule !== 'centered-on' && record?.rule !== 'centered-between') {
    return null;
  }
  // The rules are the single judge of "is it centred" — reproducing the
  // tolerance here would be a second opinion that could disagree.
  const legs = resolveDimensions([record], ctx);
  if (legs.length === 0 || !legs.every((m) => m.style === 'eq')) return null;

  const u = resolveAxis(record, ctx, axisModeFor(record.rule));
  const subject = centreOf(record.subject, ctx);
  if (!u || !subject) return null;

  // The axis of symmetry runs ACROSS the direction the legs were measured in.
  const v = perp(u);
  const s = dotXZ({ x: subject[0], z: subject[1] }, v);

  // Span both the subject and whatever it is centred on, plus an overrun.
  const datumRefs = Array.isArray(record.datum) ? record.datum : [record.datum];
  const scalars = [s];
  for (const ref of datumRefs) {
    const c = centreOf(ref, ctx);
    if (c) scalars.push(dotXZ({ x: c[0], z: c[1] }, v));
    const d = resolveDatum(ref, ctx, v);
    if (d) scalars.push(d.lo, d.hi);
  }

  const lo = Math.min(...scalars) - OVERRUN_M;
  const hi = Math.max(...scalars) + OVERRUN_M;
  const at = (t) => [subject[0] + (t - s) * v[0], subject[1] + (t - s) * v[1]];

  return {
    kind: 'centerline',
    subjectRef: record.subject,
    recordId: record.id || null,
    direction: v,
    from: at(lo),
    to: at(hi),
    label: 'CL',
  };
}

/** Every centreline the room's records mark. PURE. */
function resolveDimensionMarks(records, ctx) {
  if (!Array.isArray(records)) return [];
  const out = [];
  for (const record of records) {
    const mark = centrelineFor(record, ctx);
    if (mark) out.push(mark);
  }
  return out;
}

module.exports = { resolveDimensionMarks, centrelineFor, OVERRUN_M };
